import { DateTime } from "luxon";
import { useTranslation } from "react-i18next";

import { IDismantlingBay, IDismantlingBaySetting } from "../../models/DismantlingBay";
import {
  IPlannerDragItem,
  IPlannerVehicleRow,
  PlannerColourBy,
} from "../../models/ProductionPlanner";
import classNames from "../../utils/classNames";
import BayDropZone from "./BayDropZone";

interface ProductionLineDayColumnProps {
  date: string;
  productionLineBays: IDismantlingBay[];
  vehicles: IPlannerVehicleRow[];
  colourBy: PlannerColourBy;
  onDrop: (item: IPlannerDragItem, bayId: string, date: string) => void;
  onReorder?: (bayId: string, date: string, vehicleIds: string[]) => void;
  settingFor?: (bayId: string) => IDismantlingBaySetting | undefined;
  searchMatchIds?: Set<string>;
  isSearchActive?: boolean;
}

export default function ProductionLineDayColumn({
  date,
  productionLineBays,
  vehicles,
  colourBy,
  onDrop,
  onReorder,
  settingFor,
  searchMatchIds,
  isSearchActive = false,
}: ProductionLineDayColumnProps) {
  const { t } = useTranslation("production");
  const dt = DateTime.fromISO(date);
  const isToday = dt.hasSame(DateTime.local(), "day");
  const matchCount = searchMatchIds
    ? vehicles.filter((v) => searchMatchIds.has(v.id)).length
    : 0;

  return (
    <div
      className={classNames(
        "bg-gray-50 border rounded-md flex flex-col min-w-[260px] overflow-y-auto",
        matchCount > 0 ? "border-primary-400 ring-1 ring-primary-300" : "border-gray-200"
      )}
    >
      <div className="px-3 py-2 border-b border-gray-200 bg-white rounded-t-md flex items-center justify-between">
        <span
          className={classNames(
            "text-sm font-semibold",
            isToday ? "text-primary-700" : "text-gray-800"
          )}
        >
          {dt.toFormat("ccc")} {dt.toFormat("d LLL")}
        </span>
        <span className="flex items-center gap-x-1">
          {matchCount > 0 && (
            <span className="text-[10px] font-semibold uppercase tracking-wide bg-primary-600 text-white rounded px-1.5 py-0.5">
              {t("{{count}} found", { count: matchCount })}
            </span>
          )}
          {isToday && (
            <span className="text-[10px] font-semibold uppercase tracking-wide bg-primary-100 text-primary-700 rounded px-1.5 py-0.5">
              {t("Today")}
            </span>
          )}
        </span>
      </div>

      <div className="flex-1 flex flex-col">
        {productionLineBays.length === 0 ? (
          <div className="p-3 text-xs text-gray-400 text-center">
            {t("No production lines configured.")}
          </div>
        ) : (
          productionLineBays.map((bay) => (
            <BayDropZone
              key={bay.id}
              bay={bay}
              date={date}
              vehicles={vehicles.filter((v) => v.dismantlingBayId === bay.id)}
              colourBy={colourBy}
              onDrop={onDrop}
              onReorder={onReorder}
              variant="section"
              setting={settingFor?.(bay.id)}
              searchMatchIds={searchMatchIds}
              isSearchActive={isSearchActive}
            />
          ))
        )}
      </div>
    </div>
  );
}
